import React, { type ChangeEvent, forwardRef, useId } from "react";
import type { BaseProps } from "@purpur/common-types";
import { Label } from "@purpur/label";
import { Select } from "@purpur/select";
import c from "classnames/bind";

import { navigateToPage } from "./navigation";
import styles from "./pagination-page-size-selector.module.scss";
import { type LinkProps, type NoLinkProps } from "./types";
import { usePageSizeOptions } from "./use-page-size-options.hook";

const cx = c.bind(styles);

export type PaginationPageSizeSelectorProps = Omit<BaseProps, "children"> & {
  availablePageSizes: number[];
  onPageSizeChange: (itemsPerPage: number) => void;
  pageSize: number;
  pageSizeLabel: string;
} & (Omit<LinkProps, "onPageChange"> | Omit<NoLinkProps, "onPageChange">);

const rootClassName = "purpur-pagination-page-size-selector";

export const PaginationPageSizeSelector = forwardRef<
  HTMLDivElement,
  PaginationPageSizeSelectorProps
>(
  (
    {
      ["data-testid"]: dataTestId = "purpur-pagination-page-size-selector",
      asLink,
      availablePageSizes,
      className,
      hrefGetter,
      navigationFunction,
      onPageSizeChange,
      pageSize,
      pageSizeLabel,
      ...props
    },
    ref
  ) => {
    const selectId = useId();
    const classes = cx(className, rootClassName);
    const { options } = usePageSizeOptions(availablePageSizes);

    const onSelectionChange = (event: ChangeEvent<HTMLSelectElement>) => {
      const selectedPageSize = Number(event.target.value);
      onPageSizeChange(selectedPageSize);
      if (asLink) {
        const url = hrefGetter({ page: 1, pageSize: selectedPageSize });
        if (navigationFunction) {
          navigationFunction({ currentPage: 1, pageSize: selectedPageSize, url });
        } else {
          navigateToPage(url);
        }
      }
    };

    return (
      <div className={classes} data-testid={dataTestId} ref={ref} {...props}>
        <Label
          className={cx(`${rootClassName}__items-per-page-label`)}
          data-testid={`${dataTestId}-item-per-page-label`}
          htmlFor={selectId}
        >
          <span className={cx(`${rootClassName}__items-per-page-label-text`)}>{pageSizeLabel}</span>
        </Label>
        <Select
          className={cx(`${rootClassName}__select`)}
          data-testid={`${dataTestId}-select`}
          id={selectId}
          onChange={onSelectionChange}
          options={options}
          value={pageSize}
        />
      </div>
    );
  }
);

PaginationPageSizeSelector.displayName = "PaginationPageSizeSelector";
